import React, { lazy, Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import './eagerStyles';
import reportWebVitals from './reportWebVitals';
import { AuthProvider } from './components/AuthContext.js';
import { LoadingProvider, RouteReady } from './components/LoadingOverlay.js';
import WhatsAppButton from './components/WhatsappButton.js'
import { initGA, logPageView } from './utils/analytics'; // NEW: Import GA functions
import { AUSTRALIAN_CITIES } from './data/australianCities';
import { BRAND_SELL_PAGES } from './data/brandSellPages';

const HomeDesktop = lazy(() => import('./Home.jsx'));
const HomeDesktop1 = lazy(() => import('./Contact.jsx'));
const SellConsign = lazy(() => import('./SellConsign.jsx'));
const Catalouge = lazy(() => import('./Catalouge.jsx').then(({ Catalouge: Component }) => ({ default: Component })));
const AdminSignin = lazy(() => import('./components/AdminSignin.jsx'));
const AdminOverview = lazy(() => import('./components/AdminOverview.js'));
const ProductPage = lazy(() => import('./components/ProductPage'));
const ProductPageRedirect = lazy(() => import('./components/ProductPageRedirect'));
const AboutPage = lazy(() => import('./components/AboutPage.js'));
const ImageUpload = lazy(() => import('./components/ImageUpload.js'));
const CookiePolicyPage = lazy(() => import('./CookiePolicy.jsx'));
const ReturnPolicyPage = lazy(() => import('./ReturnPolicy.jsx'));
const TermsAndConditionsPage = lazy(() => import('./TermsAndConditions.jsx'));
const CityLandingPage = lazy(() => import('./pages/CityLandingPage.jsx'));
const BrandSellPage = lazy(() => import('./pages/BrandSellPage.jsx'));
const MagazinePage = lazy(() => import('./pages/MagazinePage.jsx'));
const MagazineArticlePage = lazy(() => import('./pages/MagazineArticlePage.jsx'));
const MagazineTopicPage = lazy(() => import('./pages/MagazineTopicPage.jsx'));

const DESKTOP_SCROLL_CLAMP_DELAYS = [0, 120, 360, 900, 1800];

const clampDesktopScrollToDocumentBounds = () => {
  if (typeof window === 'undefined' || window.innerWidth < 1000) {
    return;
  }

  const scrollRoot = document.scrollingElement || document.documentElement;

  if (!scrollRoot) {
    return;
  }

  const maxScrollTop = Math.max(0, scrollRoot.scrollHeight - scrollRoot.clientHeight);
  const currentScrollTop = scrollRoot.scrollTop;

  if (currentScrollTop > maxScrollTop + 1) {
    scrollRoot.scrollTop = maxScrollTop;

    if (document.body && document.body !== scrollRoot) {
      document.body.scrollTop = maxScrollTop;
    }
  }
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// NEW: Analytics tracker component
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track every page view when route changes
    logPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

const AppRoutes = () => (
  <>
    <ScrollToTop />
    <AnalyticsTracker /> {/* NEW: Add analytics tracker */}
    <Suspense fallback={null}>
      <RouteReady>
        <Routes>
      <Route path="/" element={<HomeDesktop/>} />
      <Route path="/contact" element={<HomeDesktop1/>} />
      <Route path="/luxandco" element={<HomeDesktop/>} />
      <Route path="/sellconsign" element={<SellConsign/>} />
      <Route path="/sell-consign" element={<SellConsign/>} />
      <Route path="/catalog/*" element={<Catalouge />} />
      <Route path="/catalog" element={<Catalouge/>} />
      <Route path="/dashboardsignin" element={<AdminSignin/>} />
      <Route path="/dashboardinventory" element={<AdminOverview/>}/>
      <Route path="/testupload" element={<ImageUpload/>}/>
      <Route path="/about" element={<AboutPage/>}/>
      <Route path="/magazine" element={<MagazinePage/>}/>
      <Route path="/magazine/topics/:topicSlug" element={<MagazineTopicPage/>}/>
      <Route path="/magazine/:slug" element={<MagazineArticlePage/>}/>
      <Route path="/cookie-policy" element={<CookiePolicyPage/>}/>
      <Route path="/return-policy" element={<ReturnPolicyPage/>}/>
      <Route path="/terms" element={<TermsAndConditionsPage/>}/>
      {AUSTRALIAN_CITIES.map((city) => (
        <Route
          key={city.cityPath}
          path={city.cityPath}
          element={<CityLandingPage citySlug={city.slug}/>}
        />
      ))}
      {AUSTRALIAN_CITIES.map((city) => (
        <Route
          key={city.sellPath}
          path={city.sellPath}
          element={<SellConsign citySlug={city.slug}/>}
        />
      ))}
      {BRAND_SELL_PAGES.map((brandPage) => (
        <Route
          key={brandPage.path}
          path={brandPage.path}
          element={<BrandSellPage brandSlug={brandPage.slug}/>}
        />
      ))}
      
      {/* SEO-friendly URLs - BEFORE the catch-all */}
      <Route path="/watches/:slug" element={<ProductPage/>}/>
      
      {/* Redirect old URLs */}
      <Route path="/product/:id" element={<ProductPageRedirect/>}/>
      
      {/* Catch-all for old ObjectId URLs - MUST BE LAST */}
      <Route path="/:id" element={<ProductPageRedirect/>}/>
        </Routes>
      </RouteReady>
    </Suspense>
  </>
);

const App = () => {
  useEffect(() => {
    window.onbeforeunload = () => {
      window.scrollTo(0, 0);
    };
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1000) {
      return undefined;
    }

    const timeoutIds = [];
    const frameIds = [];
    let cancelled = false;

    const runClamp = () => {
      frameIds.push(window.requestAnimationFrame(() => {
        if (!cancelled) {
          clampDesktopScrollToDocumentBounds();
        }
      }));
    };

    const scheduleClampPasses = () => {
      DESKTOP_SCROLL_CLAMP_DELAYS.forEach((delay) => {
        timeoutIds.push(window.setTimeout(runClamp, delay));
      });
    };

    scheduleClampPasses();

    const handleLoadLikeEvent = () => {
      if (!cancelled && window.innerWidth >= 1000) {
        scheduleClampPasses();
      }
    };

    window.addEventListener('load', handleLoadLikeEvent);
    window.addEventListener('pageshow', handleLoadLikeEvent);

    if (document.fonts?.ready) {
      document.fonts.ready
        .then(() => {
          if (!cancelled && window.innerWidth >= 1000) {
            scheduleClampPasses();
          }
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
      frameIds.forEach((frameId) => window.cancelAnimationFrame(frameId));
      window.removeEventListener('load', handleLoadLikeEvent);
      window.removeEventListener('pageshow', handleLoadLikeEvent);
    };
  }, []);

  // NEW: Initialize Google Analytics when app loads
  useEffect(() => {
    initGA();
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <LoadingProvider>
            <AppRoutes />
            <WhatsAppButton />
          </LoadingProvider>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);

reportWebVitals();
